import { useState, useEffect, useRef } from 'react';
import { feedbackConfigService } from '@/lib/feedback-config-service';
import { useAuth } from '@/providers/AuthProvider';
import { useCompany } from '@/contexts/CompanyContext';
import type { FeedbackCollectorParams } from '@/types/feedbackCollector';

/**
 * Custom hook to build the feedback collector component parameters.
 *
 * Sources userId from AuthProvider, customerName from CompanyContext,
 * and apiHost / accountToken / product from the feedback collector config.
 *
 * Stabilises the returned object reference so downstream components do not
 * re-render (and flicker) when the parent tree re-renders with identical
 * context values.
 *
 * @param feature - The feature name to collect feedback for (e.g., "Planner", "Heatmap")
 * @returns The EvoMfeApp componentParams object, or null if config is not loaded yet
 */
export function useFeedbackCollectorParams(feature: string): FeedbackCollectorParams | null {
  const [params, setParams] = useState<FeedbackCollectorParams | null>(null);
  const { userProfile, instanceId } = useAuth();
  const { currentCompany } = useCompany();

  const email = userProfile?.email ?? 'Unknown';
  const companyName = currentCompany?.name ?? instanceId ?? 'Unknown';

  const prevKeyRef = useRef<string>('');

  useEffect(() => {
    let isMounted = true;

    const key = `${feature}|${email}|${companyName}`;
    if (key === prevKeyRef.current && params !== null) return;
    prevKeyRef.current = key;

    async function buildParams() {
      try {
        const config = await feedbackConfigService.getFeedbackCollectorConfig();

        if (!isMounted) return;

        setParams({
          feature,
          accountToken: config.accountToken,
          apiHost: config.apiHost,
          customerName: companyName,
          userId: email,
          product: config.product,
        });
      } catch (error) {
        console.error('Failed to build feedback collector params:', error);
      }
    }

    buildParams();

    return () => {
      isMounted = false;
    };
  }, [feature, email, companyName, params]);

  return params;
}
